// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import FeedTopics from '@app/features/feed/state/FeedTopics';
import {isFeedTopicsEnabled} from '@app/features/feed/utils/FeedUtils';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {FeedTopicListResponse, FeedTopicResponse} from '@fluxer/schema/src/domains/feed/FeedTopicSchemas';

const logger = new Logger('FeedTopicCommands');

// When the feed page opens, and again only once the list is older than FEED_TOPICS_STALE_MS; `force` after the server
// refused a topic the list still offered (it ended or was removed). Nothing is asked while feature_feed_topics is off.
export async function loadTopics({force = false}: {force?: boolean} = {}): Promise<void> {
	if (!isFeedTopicsEnabled()) return;
	const session = FeedTopics.beginLoad(force);
	if (session === null) return;
	try {
		const response = await http.get<FeedTopicListResponse>(Endpoints.FEED_TOPICS);
		FeedTopics.handleLoaded(session, response.body ?? []);
	} catch (error) {
		logger.warn('Failed to load feed topics:', error);
		FeedTopics.handleLoadFailed(session, error instanceof HttpError && error.status === 403 ? 'forbidden' : 'error');
	}
}

const pendingTopics = new Set<string>();

// A topic page the live list does not hold (an ended topic opened from a post's tag); its posts load either way, so a
// failure only leaves the page without its header.
export async function loadTopic(topicId: string): Promise<void> {
	if (!isFeedTopicsEnabled() || FeedTopics.get(topicId) || pendingTopics.has(topicId)) return;
	const session = FeedTopics.getSession();
	pendingTopics.add(topicId);
	try {
		const response = await http.get<FeedTopicResponse>(Endpoints.FEED_TOPIC(topicId));
		if (response.body) FeedTopics.handleTopicLoaded(session, response.body);
	} catch (error) {
		logger.warn(`Failed to load feed topic ${topicId}:`, error);
	} finally {
		pendingTopics.delete(topicId);
	}
}
